import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "section lottery-section" }

import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue'
import LotteryCard from '@/components/LotteryCard.vue'
import LotteryFilters from '@/components/LotteryFilters.vue'
import SectionHeader from '@/components/base/SectionHeader.vue'
import type { UpcomingDraw } from '@/types/lottery'
import { useIntegrationMode } from '@/composables/useIntegrationMode'


const __default__ = {
  name: 'LotterySection'
}

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
    title: { default: '' },
    lotteries: {},
    isFavorited: { type: Function, default: () => false },
    showFilters: { type: Boolean, default: true },
    layout: { default: 'horizontal' }
  },
  emits: ["favorite", "bet", "removeLottery"],
  setup(__props: any, { emit: __emit }) {

const { isLite } = useIntegrationMode()

const props = __props

const cardLayout = computed(() => (isLite.value ? 'horizontal' : props.layout))

const canScrollPrev = ref(false)
const canScrollNext = ref(false)
const isMobile = ref(false)
let resizeObserver: ResizeObserver | null = null

const getScrollStep = () => {
  if (!gridRef.value) return isMobile.value ? 168 : 222.78

  const firstItem = gridRef.value.firstElementChild as HTMLElement | null
  if (!firstItem) return isMobile.value ? 168 : 222.78

  const styles = window.getComputedStyle(gridRef.value)
  const gapValue = parseFloat(styles.columnGap || styles.gap || '0')

  return firstItem.getBoundingClientRect().width + gapValue
}

const updateNavVisibility = () => {
  if (gridRef.value && props.layout === 'vertical') {
    const { scrollWidth, clientWidth, scrollLeft } = gridRef.value
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    const threshold = 2

    canScrollPrev.value = scrollLeft > threshold
    canScrollNext.value = maxScrollLeft - scrollLeft > threshold
  } else {
    canScrollPrev.value = false
    canScrollNext.value = false
  }
}

const scrollPrev = () => {
  if (gridRef.value) {
    gridRef.value.scrollBy({ left: -getScrollStep(), behavior: 'smooth' })
    window.setTimeout(updateNavVisibility, 250)
  }
}

const scrollNext = () => {
  if (gridRef.value) {
    gridRef.value.scrollBy({ left: getScrollStep(), behavior: 'smooth' })
    window.setTimeout(updateNavVisibility, 250)
  }
}

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
  updateNavVisibility()
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)

  nextTick(() => {
    updateNavVisibility()

    if (gridRef.value && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateNavVisibility()
      })
      resizeObserver.observe(gridRef.value)
    }

    window.setTimeout(updateNavVisibility, 100)
    window.setTimeout(updateNavVisibility, 350)
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  resizeObserver?.disconnect()
})

const emit = __emit

// Local copy with _removing property
const localLotteries = ref((props.lotteries || []).map(l => ({ ...l, _removing: false } as UpcomingDraw & { _removing: boolean })));

// Filter states
const searchQuery = ref('');
const selectedCountry = ref('');

/** Countries that currently have draws in this section (for mobile filter UX). */
const activeCountrySlugs = computed(() => {
  const slugs = new Set<string>();
  for (const draw of localLotteries.value) {
    if (draw.country) slugs.add(String(draw.country).toLowerCase());
  }
  return Array.from(slugs);
});

// Filtered lotteries based on search and country filters
const filteredLotteries = computed(() => {
  let filtered = [...localLotteries.value];

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(draw => 
      (draw.game_name && draw.game_name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
      (draw.country && draw.country.toLowerCase().includes(searchQuery.value.toLowerCase()))
    );
  }

  // Apply country filter
  if (selectedCountry.value) {
    filtered = filtered.filter(draw => draw.country === selectedCountry.value);
  }

  return filtered;
});

// Filter handlers
const handleSearch = (query: string) => {
  searchQuery.value = query;
};

const handleCountryFilter = (country: string) => {
  selectedCountry.value = country;
};

watch(() => props.lotteries, (newVal) => {
  localLotteries.value = (newVal || []).map(l => ({ ...l, _removing: false }));
  nextTick(() => {
    updateNavVisibility()
  })
});

watch(filteredLotteries, () => {
  nextTick(() => {
    updateNavVisibility()
  })
})

const handleRemove = (lotteryToRemove: UpcomingDraw) => {
  const idx = localLotteries.value.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    localLotteries.value[idx]._removing = true;
  }
};

const finalizeRemove = (lotteryToRemove: UpcomingDraw) => {
  const idx = localLotteries.value.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    emit('removeLottery', lotteryToRemove);
    localLotteries.value.splice(idx, 1);
  }
};

const gridRef = ref<HTMLElement | null>(null)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _renderSlot(_ctx.$slots, "header", {}, () => [
      _createVNode(SectionHeader, {
        title: _ctx.title,
        variant: "simple"
      }, null, 8, ["title"])
    ]),
    (_ctx.showFilters)
      ? (_openBlock(), _createBlock(LotteryFilters, {
          key: 0,
          "active-country-slugs": activeCountrySlugs.value,
          onSearch: handleSearch,
          onFilterCountry: handleCountryFilter
        }, null, 8, ["active-country-slugs"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["lottery-container", { 'has-swiper': cardLayout.value === 'vertical' }])
    }, [
      (cardLayout.value === 'vertical')
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: _normalizeClass(["swiper-nav swiper-nav-prev", { 'is-visible': canScrollPrev.value }]),
            onClick: scrollPrev
          }, _cache[0] || (_cache[0] = [
            _createElementVNode("svg", {
              width: "10",
              height: "16",
              viewBox: "0 0 10 16",
              fill: "none"
            }, [
              _createElementVNode("path", {
                d: "M8 2L2 8L8 14",
                stroke: "currentColor",
                "stroke-width": "2",
                "stroke-linecap": "round",
                "stroke-linejoin": "round"
              })
            ], -1)
          ]), 2))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        ref_key: "gridRef",
        ref: gridRef,
        class: _normalizeClass(["lottery-grid", [cardLayout.value]]),
        onScroll: updateNavVisibility
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredLotteries.value, (lottery) => {
          return (_openBlock(), _createElementBlock("div", {
            key: `${lottery.game_slug}-${lottery.id}`
          }, [
            _createVNode(_Transition, {
              name: "fade",
              onAfterLeave: () => finalizeRemove(lottery)
            }, {
              default: _withCtx(() => [
                (!lottery._removing)
                  ? (_openBlock(), _createBlock(LotteryCard, {
                      key: 0,
                      lottery: lottery,
                      layout: cardLayout.value,
                      "is-favorited": _ctx.isFavorited,
                      onFavorite: () => _ctx.$emit('favorite', lottery),
                      onBet: () => _ctx.$emit('bet', lottery),
                      onRemove: ($event: any) => (handleRemove(lottery))
                    }, null, 8, ["lottery", "layout", "is-favorited", "onFavorite", "onBet", "onRemove"]))
                  : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1032, ["onAfterLeave"])
          ]))
        }), 128))
      ], 34),
      (cardLayout.value === 'vertical')
        ? (_openBlock(), _createElementBlock("button", {
            key: 1,
            class: _normalizeClass(["swiper-nav swiper-nav-next", { 'is-visible': canScrollNext.value }]),
            onClick: scrollNext
          }, _cache[1] || (_cache[1] = [
            _createElementVNode("svg", {
              width: "10",
              height: "16",
              viewBox: "0 0 10 16",
              fill: "none"
            }, [
              _createElementVNode("path", {
                d: "M2 2L8 8L2 14",
                stroke: "currentColor",
                "stroke-width": "2",
                "stroke-linecap": "round",
                "stroke-linejoin": "round"
              })
            ], -1)
          ]), 2))
        : _createCommentVNode("", true)
    ], 2)
  ]))
}
}

})